import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Segmented, Table, Typography } from 'antd';

import { useAccess } from '../../auth/AccessContext.jsx';
import StatusTag from '../../components/StatusTag.jsx';
import { formatUnits } from '../../format';
import { STATUS_KIND } from '../../status/statusHelp.js';
import { HOLDING_OUTCOMES, OUTCOME_COLOUR, readHoldingChecks, unitsPair } from './batchDetail.js';

const { Text } = Typography;

/** File units against the register's, the register's in red where they differ. */
const Pair = ({ file, register }) => {
    const pair = unitsPair(file, register);
    if (pair.file === null && pair.register === null) return <Text type="secondary">—</Text>;
    return (
        <span className="holding-pair">
            <span>{pair.file === null ? '—' : formatUnits(pair.file)}</span>
            <Text type={pair.differs ? 'danger' : 'secondary'}>{' / ' + (pair.register === null ? '—' : formatUnits(pair.register))}</Text>
        </span>
    );
};

/**
 * One page of holding checks: the file's units against the register's, in
 * total and by the ageing split (young: acquired within 12 calendar months
 * of the file's date, the file's own definition - IP-108).
 *
 * @param {{ checks: object[], loading?: boolean }} props
 */
export const HoldingChecksTable = ({ checks, loading = false }) => {
    const columns = [
        { title: 'Row', dataIndex: 'rowNo', key: 'rowNo', width: 70, render: (v) => v ?? '—' },
        { title: 'Strategy', dataIndex: 'strategyRef', key: 'strategyRef', render: (v) => v ?? '—' },
        { title: 'Account', dataIndex: 'accountCode', key: 'accountCode', render: (v) => v ?? '—' },
        { title: 'Fund', dataIndex: 'fundId', key: 'fundId', width: 90 },
        { title: 'Units (file / register)', key: 'units', align: 'right', render: (_, c) => <Pair file={c.fileUnits} register={c.registerUnits} /> },
        { title: 'Under 1 year', key: 'young', align: 'right', render: (_, c) => <Pair file={c.fileYoungUnits} register={c.registerYoungUnits} /> },
        { title: 'Over 1 year', key: 'old', align: 'right', render: (_, c) => <Pair file={c.fileOldUnits} register={c.registerOldUnits} /> },
        { title: 'Outcome', dataIndex: 'outcome', key: 'outcome', width: 140, render: (v) => <StatusTag kind={STATUS_KIND.HOLDING_CHECK} code={v} color={OUTCOME_COLOUR[v]} /> }
    ];
    return (
        <Table rowKey="checkId" size="small" loading={loading} columns={columns} dataSource={checks} pagination={false}
            scroll={{ x: 'max-content' }} locale={{ emptyText: 'Nothing to show for this outcome' }} />
    );
};

/**
 * A DAILY holdings file against the register, once accepted (IP-108, D-A:
 * compared, never posted): the breaks, unknown clients and holders the file
 * left out by default, any outcome on choice, the server's 100 a page with
 * Load more (GET intake/batches/{id}/holding-checks, its checks the answer's
 * items - batchDetail.js readHoldingChecks).
 *
 * @param {{ batchId: string }} props
 */
const HoldingChecks = ({ batchId }) => {
    const { api } = useAccess();
    const [view, setView] = useState(HOLDING_OUTCOMES[0].value);
    const [checks, setChecks] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const latest = useRef(0);

    const read = useCallback(async (cursor = null) => {
        const ticket = ++latest.current;
        setLoading(true);
        setError(null);
        try {
            const page = await readHoldingChecks(api, batchId, view, cursor);
            if (ticket !== latest.current) return;
            setChecks((current) => (cursor ? [...current, ...page.checks] : page.checks));
            setNextCursor(page.nextCursor);
        } catch (e) {
            if (ticket === latest.current) setError(e.message);
        } finally {
            if (ticket === latest.current) setLoading(false);
        }
    }, [api, batchId, view]);

    useEffect(() => {
        read();
    }, [read]);

    return (
        <div className="uploads-holding-checks">
            <h4 className="panel-title">Against the register</h4>
            <Segmented size="small" value={view} onChange={setView} options={HOLDING_OUTCOMES.map(({ value, label }) => ({ value, label }))} />
            {error && <Alert type="error" showIcon message={error} className="auth-alert" />}
            <HoldingChecksTable checks={checks} loading={loading} />
            {nextCursor && <Button size="small" onClick={() => read(nextCursor)} loading={loading}>Load more</Button>}
        </div>
    );
};

export default HoldingChecks;
